// AI-CONTEXT: See docs/features/api-data-fetching.md
// Haalt de antwoorden op de andere velden van een activiteit op, voor de
// tooltip van een outputgrafiek (updateverzoek Team Sportclub, 2026-10-14).
//
// Nodig omdat useOutputData alleen de output-regels ophaalt (syncType
// 'OutputEvent'); de velden van het registratieformulier zijn losse
// EventResults-regels, per veld op te halen met syncType 'Event' — dezelfde
// weg als de Activiteit-widget, inclusief gekoppelde projecten.

import { useContext, useEffect, useState } from 'react'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { callFetchHierarchyData } from '../../hooks/useHierarchyData'
import PublicReportContext from '../../StateManagment/PublicReportContext'
import notDeleted from '../../helpers/notDeleted'
import { TOOLTIP_FIELD_TYPES, normalizeChoiceRecords } from '../../helpers/dataHelpers/summarizeOutputFields'
import fetchOptionsById from './fetchOptionsById'

/** @typedef {import('../../helpers/dataHelpers/summarizeOutputFields').FieldResult} FieldResult */

/**
 * @typedef {object} OutputFieldResultsState
 * @property {'idle'|'loading'|'ready'|'error'} status - 'idle': niets op te halen
 *   (geen activiteit of getalweergave)
 * @property {FieldResult[]} fieldResults
 */

const CHOICE_TYPES = ['multiple-one', 'multiple-multiple']

/**
 * Sjabloonmodus: velden en antwoorden komen uit het vooraf opgehaalde bestand.
 * Net als de Activiteit-widget het eerste registratieformulier van de activiteit.
 * Keuzevelden vallen hier weg: het sjabloonbestand bevat de keuze-opties niet,
 * en zonder optie is een optienaam niet van vrije 'Anders'-tekst te
 * onderscheiden (normalizeChoiceRecords).
 *
 * @param {{eventForms?: object[], eventFields?: object[], eventResults?: object[]}|null} blob
 * @param {string} activityId
 * @returns {FieldResult[]}
 */
export const fieldResultsFromTemplate = (blob, activityId) => {
    const form = (blob?.eventForms || []).filter(notDeleted).find(f => f.ActivityID === activityId)
    if (!form) return []
    const results = blob?.eventResults || []
    return (blob?.eventFields || [])
        .filter(notDeleted)
        .filter(f => f.EventId === form.ID)
        .filter(f => !CHOICE_TYPES.includes(f.Type))
        .map(field => ({ field, records: results.filter(r => r.FieldID === field.ID) }))
}

/**
 * Live: het registratieformulier van de activiteit, zijn velden, en per veld
 * de antwoorden in de periode van de grafiek.
 *
 * @param {string} activityId
 * @param {number} startMs
 * @param {number} endMs
 * @param {import('../../StateManagment/PublicReportContext').PublicReport|null} publicReport - In
 *   een openbaar gedeelde rapportage: via de openbare poort (getPublicLiveReportData)
 * @returns {Promise<FieldResult[]>}
 */
const fetchFieldResults = async (activityId, startMs, endMs, publicReport) => {
    const formsSnap = await db.collection('EventForms')
        .where('CompagnyID', '==', client || '')
        .where('ActivityID', '==', activityId)
        .get()
    const form = formsSnap.docs.map(d => d.data()).filter(notDeleted)[0]
    if (!form) return []

    const fieldsSnap = await db.collection('EventFields')
        .where('CompagnyID', '==', client || '')
        .where('EventId', '==', form.ID)
        .get()
    // Alleen de soorten die de tooltip samenvat: geen Cloud Function-aanroep
    // voor een open vraag of een datum die toch niet getoond wordt.
    const fields = fieldsSnap.docs.map(d => d.data()).filter(notDeleted)
        .filter(f => TOOLTIP_FIELD_TYPES.includes(f.Type))

    return Promise.all(fields.map(async (field) => {
        const { records } = await callFetchHierarchyData({
            syncType: 'Event',
            id: field.ID,
            startDate: startMs,
            endDate: endMs,
            publicReport,
        })
        // Keuzevelden: de naam komt uit de optie, nooit uit de opgeslagen tekst
        // (daar staat bij 'Anders, namelijk' vrije tekst van de invuller).
        if (!CHOICE_TYPES.includes(field.Type)) return { field, records: records || [] }
        const optionsById = await fetchOptionsById(records || [])
        return { field, records: normalizeChoiceRecords(records || [], optionsById) }
    }))
}

/**
 * @param {object} args
 * @param {string} [args.activityId] - ActivityID van de output; '' zolang onbekend
 * @param {number} [args.startMs] - Zelfde periode als de output-regels (useOutputData)
 * @param {number} [args.endMs]
 * @param {boolean} [args.enabled=true] - false voor een weergave zonder tooltip
 * @param {object|null} [args.templateOutputData] - Sjabloonbestand; dan geen Firestore
 * @returns {OutputFieldResultsState}
 */
const useOutputFieldResults = ({ activityId, startMs, endMs, enabled = true, templateOutputData = null }) => {
    const publicReport = useContext(PublicReportContext)
    const [state, setState] = useState(/** @type {OutputFieldResultsState} */ ({ status: 'idle', fieldResults: [] }))

    useEffect(() => {
        if (!enabled || !activityId) {
            setState({ status: 'idle', fieldResults: [] })
            return undefined
        }

        if (templateOutputData) {
            setState({ status: 'ready', fieldResults: fieldResultsFromTemplate(templateOutputData, activityId) })
            return undefined
        }

        if (startMs == null || endMs == null) {
            setState({ status: 'idle', fieldResults: [] })
            return undefined
        }

        let cancelled = false
        setState({ status: 'loading', fieldResults: [] })
        // In een openbaar gedeelde rapportage gaan de antwoorden via de openbare
        // poort; die laat alleen de velden van outputs uit dát rapport door.
        // Formulier en velden zelf staan op `read: if true`, net als de vragen
        // in fase 1 (docs/features/public-livereport-data.md).
        fetchFieldResults(activityId, startMs, endMs, publicReport || null)
            .then((fieldResults) => {
                if (!cancelled) setState({ status: 'ready', fieldResults })
            })
            .catch((err) => {
                console.error('useOutputFieldResults: veldantwoorden niet op te halen', err)
                if (!cancelled) setState({ status: 'error', fieldResults: [] })
            })
        return () => { cancelled = true }
    }, [activityId, startMs, endMs, enabled, templateOutputData, publicReport])

    return state
}

export default useOutputFieldResults
